import { QueryFactory } from '@lmt/shared-types';
import { sendRefreshTokenRequest } from '@lmt/unicorn-shared/app/api/auth/auth-client';

import { miniProfilerInterceptor } from '@lmt/unicorn-shared/app/helpers/MiniProfiler';
import { sessionAxiosInterceptor } from '@lmt/unicorn-shared/app/helpers/session-helper';
import { injectTenantDomainInterceptor } from '@lmt/unicorn-shared/app/helpers/tenant-domain-helper';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import i18n from 'i18next';
import { FeatureFlags } from '../constants/featureFlags';
import { cleanupOnLogout } from '../features/auth/operations';

function createInjectTimezoneInterceptor() {
  const timeZoneOffset = new Date().getTimezoneOffset();
  return async (config: InternalAxiosRequestConfig) => {
    config.headers = config.headers ?? {};
    config.headers.Timezone = timeZoneOffset.toString();
    return config;
  };
}

const requestLoggingInterceptor = (config: InternalAxiosRequestConfig) => {
  console.log('http.request', config.method, config.url);
  return config;
};
export const responseErrorLoggingInterceptor = async (error: {
  response: AxiosResponse<any>;
  request: InternalAxiosRequestConfig;
  toJSON(): string;
}) => {
  console.error(
    'http.request.error',
    JSON.stringify(error?.toJSON?.()),
    error.response?.data,
  );

  throw error;
};

async function injectLanguageInterceptor(config: InternalAxiosRequestConfig) {
  const language = i18n.language;
  config.headers = config.headers ?? {};
  config.headers['Accept-Language'] = language;
  return config;
}

export function initializeAxios() {
  // will be needed for nginx if we'd use it
  // axios.defaults.headers.common = {
  //   'X-FORWARDED-HOST': X_FORWARDED_HOST,
  // };

  createAuthRefreshInterceptor(
    axios,
    async (failedRequest: any): Promise<void> => {
      try {
        await sendRefreshTokenRequest();
      } catch (e: any) {
        if (e.message === 'Login_Unknown_Failure') {
          cleanupOnLogout();
        }
        throw e;
      }
    },
    {
      pauseInstanceWhileRefreshing: true,
    },
  );
  axios.interceptors.request.use(injectTenantDomainInterceptor);
  axios.interceptors.request.use(createInjectTimezoneInterceptor());
  axios.interceptors.request.use(injectLanguageInterceptor);
  axios.interceptors.request.use(sessionAxiosInterceptor);
  axios.interceptors.request.use(requestLoggingInterceptor);
  axios.interceptors.response.use(null, responseErrorLoggingInterceptor);
  axios.interceptors.response.use(null, blobResponseErrorInterceptor);

  if (FeatureFlags.EnableMiniProfiler) {
    axios.interceptors.response.use(miniProfilerInterceptor, undefined);
  }
  return axios;
}

/**
 * Intercepts error responses for blob requests (e.g. file downloading) and parses the response
 * since it's probably JSON which isn't handled if `request.responseType === 'blob'`
 * See for details: https://stackoverflow.com/questions/56286368/how-can-i-read-http-errors-when-responsetype-is-blob-in-axios-with-vuejs
 * Fixes: #48233: No error message when document is not found
 */
export const blobResponseErrorInterceptor = async (error: {
  response: AxiosResponse<any>;
  request: InternalAxiosRequestConfig;
}) => {
  const response = error.response;
  const request = error.request;
  const data = response?.data;

  if (request?.responseType === 'blob' && data instanceof Blob && data.type) {
    const text = await data.text();

    response.data = data.type.toLowerCase().includes('json')
      ? JSON.parse(text)
      : text;
  }

  throw error;
};
